(() => {
  const SELECTOR = '.rrr-mobile-header';

  class RRRMobileHeader {
    constructor(root) {
      this.root = root;
      this.toggle = root.querySelector('[data-rrr-mobile-toggle]');
      this.closeButton = root.querySelector('[data-rrr-mobile-close]');
      this.drawer = root.querySelector('[data-rrr-mobile-drawer]');
      this.overlay = root.querySelector('[data-rrr-mobile-overlay]');
      this.searchToggle = root.querySelector('[data-rrr-mobile-search-toggle]');
      this.searchPanel = root.querySelector('[data-rrr-mobile-search]');
      this.tabs = [...root.querySelectorAll('[data-rrr-mobile-tab]')];
      this.audienceItems = [...root.querySelectorAll('[data-audience]')];
      this.activeAudience = 'men';
      this.lastFocused = null;
      this.boundKeydown = this.onKeydown.bind(this);
      this.boundScroll = this.onScroll.bind(this);
      this.boundResize = this.onResize.bind(this);

      if (!this.toggle || !this.drawer || !this.overlay) return;

      this.toggle.addEventListener('click', () => this.toggleDrawer());
      this.closeButton?.addEventListener('click', () => this.close());
      this.overlay.addEventListener('click', () => this.close());
      this.searchToggle?.addEventListener('click', () => this.toggleSearch());

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.setAudience(tab.dataset.rrrMobileTab));
      });

      this.setAudience(this.activeAudience);
      window.addEventListener('scroll', this.boundScroll, { passive: true });
      window.addEventListener('resize', this.boundResize, { passive: true });
      this.onScroll();
    }

    isOpen() {
      return this.drawer.classList.contains('is-open');
    }

    toggleDrawer() {
      this.isOpen() ? this.close() : this.open();
    }

    open() {
      this.lastFocused = document.activeElement;
      this.overlay.hidden = false;

      requestAnimationFrame(() => {
        this.overlay.classList.add('is-visible');
        this.drawer.classList.add('is-open');
      });

      this.toggle.setAttribute('aria-expanded', 'true');
      this.drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('rrr-mobile-drawer-open');
      document.addEventListener('keydown', this.boundKeydown);

      window.setTimeout(() => this.closeButton?.focus({ preventScroll: true }), 30);
    }

    close() {
      if (!this.isOpen()) return;

      this.overlay.classList.remove('is-visible');
      this.drawer.classList.remove('is-open');
      this.toggle.setAttribute('aria-expanded', 'false');
      this.drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('rrr-mobile-drawer-open');
      document.removeEventListener('keydown', this.boundKeydown);

      const duration = parseFloat(
        getComputedStyle(this.root).getPropertyValue('--rrr-speed')
      ) || 260;

      window.setTimeout(() => {
        this.overlay.hidden = true;
      }, duration);

      this.lastFocused?.focus?.({ preventScroll: true });
    }

    toggleSearch() {
      if (!this.searchPanel) return;
      const opening = this.searchPanel.hidden;
      this.searchPanel.hidden = !opening;

      if (opening) {
        window.setTimeout(() => {
          this.searchPanel.querySelector('input')?.focus({ preventScroll: true });
        }, 10);
      }
    }

    setAudience(audience) {
      this.activeAudience = audience;

      this.tabs.forEach((tab) => {
        const active = tab.dataset.rrrMobileTab === audience;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', String(active));
      });

      this.audienceItems.forEach((item) => {
        const value = item.dataset.audience;
        item.hidden = !(value === audience || value === 'both');
      });
    }

    onScroll() {
      this.root.classList.toggle('is-scrolled', window.scrollY > 8);
    }

    onResize() {
      if (window.innerWidth >= 990 && this.isOpen()) this.close();
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        this.close();
        return;
      }

      if (event.key !== 'Tab' || !this.isOpen()) return;

      const focusable = [
        ...this.drawer.querySelectorAll(
          'a[href], button:not([disabled]), summary, input:not([disabled]), [tabindex]:not([tabindex="-1"])'
        ),
      ].filter((element) => !element.hidden && element.offsetParent !== null);

      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  const init = (scope = document) => {
    scope.querySelectorAll(SELECTOR).forEach((root) => {
      if (root.dataset.rrrMobileInitialized === 'true') return;
      root.dataset.rrrMobileInitialized = 'true';
      new RRRMobileHeader(root);
    });
  };

  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
